import React, { useContext } from 'react';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import { GlobalTimeslots, OpeningEntityTypeEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import * as yup from 'yup';
import ManageTimeslot, { timeslotValidationSchema } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import useSidebarControl, { SidebarControlProps } from '@hooks/useSidebarControl/useSidebarControl.tsx';

/** Which leg of the transport this panel edits. */
export type TimeslotLeg = 'loading' | 'unloading';

interface TimeslotFormData {
  slots?: GlobalTimeslots;
}

interface ComponentProps {
  supplierNotificationId: string;
  leg: TimeslotLeg;
  /** Timeslots of the edited leg. */
  slots?: GlobalTimeslots;
  /**
   * Timeslots of the *other* leg, echoed back unchanged. The endpoint takes a full representation
   * (both legs are required in SNUpdateDataTimeslotsInDTO), so editing one leg still has to send the
   * other. Read from the loaded detail, so a concurrent change to the other leg is overwritten —
   * the same exposure the previous combined panel had.
   */
  otherSlots?: GlobalTimeslots;
  locationId: string;
  locationTimezoneId?: string;
}

// Module scope on purpose: a component declared inside the hook is a new type on every render of the
// owning page, and React remounts it — losing the form and refetching. See useSidebarControl.
const EditSupplierNotificationTimeslotSidebar: React.FC<ComponentProps & SidebarControlProps> = ({
  supplierNotificationId,
  leg,
  slots,
  otherSlots,
  locationId,
  locationTimezoneId,
  visible,
  onHide,
  onComplete,
}) => {
  const { updateNotificationSupplierUpdateDataForTimeslots } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();

  const names = proxiedPropertiesOf<TimeslotFormData>();

  const form = useForm<TimeslotFormData>(
    yup.object().shape({
      slots: timeslotValidationSchema(),
    }),
    { slots },
    values => {
      updateNotificationSupplierUpdateDataForTimeslots(
        supplierNotificationId,
        {
          loadingDeliverySlot: leg === 'loading' ? values.slots : otherSlots,
          unloadingDeliverySlot: leg === 'unloading' ? values.slots : otherSlots,
        },
        () => {
          onHide();
          onComplete();
        },
        null,
        { onValidationFailed: setBeValidationErrors },
      );
    },
    false,
    false,
  );

  return (
    <RingilSidebar
      visible={visible}
      heading={
        leg === 'loading'
          ? tr('EditSupplierNotificationTimeslotSidebar.editPickupDate', 'Edit pickup date')
          : tr('EditSupplierNotificationTimeslotSidebar.editDeliveryDate', 'Edit delivery date')
      }
      onBack={onHide}
      onContinue={() => form.validateAndSend()}
    >
      {form.values && (
        <>
          <BackendValidationComponent />
          <ManageTimeslot
            entityId={locationId}
            entityType={OpeningEntityTypeEnum.LOCATION}
            timeslot={form.values.slots}
            onChange={v => form.setFieldValue(names.slots, { ...v, timezoneId: locationTimezoneId })}
            validationErrors={form.validationErrors}
            validationPrefix={'slots.'}
            dataTestId={leg === 'loading' ? 'sn-detail-edit-loading-timeslot' : 'sn-detail-edit-unloading-timeslot'}
          />
        </>
      )}
    </RingilSidebar>
  );
};

export const useCreateEditSupplierNotificationTimeslotSidebar = (onComplete: () => void) => {
  const { sidebarProps, mountKey, setVisible } = useSidebarControl(onComplete);
  return { EditSupplierNotificationTimeslotSidebar, sidebarProps, mountKey, setVisible };
};
